import React from 'react';
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ThemeProvider,
} from '@mui/material';
import responseInputMuiTheme from 'pages/Forms/Theme/responseInputMuiTheme';
import { AlertMessage } from 'pages/Forms/components/AlertMessage/AlertMessage';
import { selectedActionInputStyles } from 'pages/Forms/Theme/responseInputStyles';
import ClearSelection from 'pages/Forms/RegisterForm/CustomFormBuilder/Header/Preview/components/ClearSelection';
import useOptionsInputs from 'pages/Forms/RegisterForm/CustomFormBuilder/Header/Preview/TableFormFieldPreview/TableCellPreview/hooks/useOptionsInputs';
import { TableFieldPreviewProps } from 'pages/Forms/RegisterForm/CustomFormBuilder/Header/type/preview.type';
import { secondary } from 'assets/colors';
import { ChoiceImagePreview } from 'pages/Forms/RegisterForm/CustomFormBuilder/Header/Preview/ChoiceImagePreview';

interface ChoicePreviewProps extends TableFieldPreviewProps {
  type?: 'yesno' | 'yesnona' | 'radiogroup';
}

const ChoicePreview: React.FC<ChoicePreviewProps> = ({
  field,
  tableId,
  tableType,
  rowIndex,
  colIndex,
  updateAnswer,
  updatePreviewColumn,
  updateAnswerInPrefilledElements,
  type = 'radiogroup',
  shouldBeReadOnly,
}) => {
  const theme = responseInputMuiTheme;

  const {
    textFieldRef,
    otherText,
    selectedValues,
    showActionMessage,
    handleChange,
    handleOtherTextChange,
    clearSelection,
    isSelected,
  } = useOptionsInputs({
    field,
    updateAnswer,
    tableId,
    tableType,
    rowIndex,
    colIndex,
    updatePreviewColumn,
    updateAnswerInPrefilledElements,
    inputType: type,
  });

  const selectedValue = selectedValues[0] || '';

  return (
    <ThemeProvider theme={theme}>
      <RadioGroup
        value={selectedValue}
        onChange={(e) => handleChange(e.target.value)}
      >
        {field?.choice?.map((choice) => (
          <Stack key={choice.id} spacing={1}>
            <FormControlLabel
              value={choice.id}
              control={
                <Radio color="secondary" checked={isSelected(choice.id)} />
              }
              label={
                <Stack>
                  <ChoiceImagePreview choice={choice} />
                  {choice.label}
                </Stack>
              }
              sx={{
                ...(isSelected(choice.id) && choice.action
                  ? selectedActionInputStyles(theme)
                  : {}),
                '&.Mui-disabled': {
                  '& .MuiRadio-root.Mui-checked': {
                    color: `${secondary.main}`, // Show secondary color if shouldBeReadOnly is true causing the options to be disabled
                  },
                },
              }}
              disabled={shouldBeReadOnly}
            />
            {choice.isOtherField && isSelected(choice.id) && (
              <Box mt={1}>
                <TextField
                  fullWidth
                  inputRef={textFieldRef}
                  onChange={(e) => handleOtherTextChange(e.target.value)}
                  placeholder="Your answer"
                  value={otherText}
                  onFocus={(e) => e.target.select()}
                  disabled={shouldBeReadOnly}
                />
              </Box>
            )}
          </Stack>
        ))}
      </RadioGroup>

      {showActionMessage && field.actionMessage && (
        <Box mt={1}>
          <AlertMessage alertMessage={field.actionMessage} />
        </Box>
      )}

      {!field.isRequired && selectedValue && !shouldBeReadOnly && (
        <ClearSelection handleClear={clearSelection} />
      )}
    </ThemeProvider>
  );
};

export default ChoicePreview;
